import React, { useCallback, useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHome, faSearch, faTrash } from "@fortawesome/free-solid-svg-icons";
import AdminLayout from "./AdminLayout";
import "./admin.css";

function adminFetch(path, opts = {}) {
  const token = localStorage.getItem("adminToken");
  return fetch(path, {
    ...opts,
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json", ...(opts.headers || {}) },
  });
}

export default function AdminProperties() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [deletingId, setDeletingId] = useState(null);

  const role = localStorage.getItem("adminRole") || "";
  const canDelete = role === "super_admin" || role === "support";

  const load = useCallback(async (pg, q, status) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(pg), limit: "20" });
      if (q) params.set("search", q);
      if (status) params.set("status", status);

      const res = await adminFetch(`/api/admin/properties?${params.toString()}`);
      const data = await res.json();
      if (res.ok) {
        setRows(data.properties || []);
        setTotal(data.total || 0);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(page, search, statusFilter);
  }, [page, search, statusFilter, load]);

  const handleDelete = async (property) => {
    if (!canDelete || !property?.id) return;
    const label = property.property_name || `#${property.id}`;
    if (!window.confirm(`Delete property "${label}"? This cannot be undone.`)) return;

    setDeletingId(property.id);
    try {
      const res = await adminFetch(`/api/admin/properties/${property.id}`, { method: "DELETE" });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(payload.message || "Failed to delete property.");
      await load(page, search, statusFilter);
    } catch (err) {
      alert(err.message || "Failed to delete property.");
    } finally {
      setDeletingId(null);
    }
  };

  const totalPages = Math.ceil(total / 20) || 1;

  return (
    <AdminLayout pageTitle="Properties">
      <div className="adm-toolbar">
        <div className="adm-search">
          <FontAwesomeIcon icon={faSearch} />
          <input
            type="text"
            placeholder="Search by property, address, or owner..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />
        </div>

        <select
          className="adm-select"
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPage(1);
          }}
        >
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="unlisted">Unlisted</option>
          <option value="rented">Rented</option>
        </select>
      </div>

      <div className="adm-card">
        <div className="adm-card-header">
          <h3>Properties</h3>
          <span style={{ fontSize: ".8rem", color: "var(--adm-muted)" }}>{total} total</span>
        </div>

        {loading && (
          <div className="adm-spinner-wrap">
            <div className="spinner-border text-secondary" />
          </div>
        )}

        {!loading && rows.length === 0 && (
          <div className="adm-empty">
            <FontAwesomeIcon icon={faHome} />
            <p>No properties found.</p>
          </div>
        )}

        {!loading && rows.length > 0 && (
          <>
            <div className="adm-table-wrap">
              <table className="adm-table">
                <thead>
                  <tr>
                    <th>Property</th>
                    <th>Owner</th>
                    <th>Status</th>
                    <th>Price</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((p) => {
                    const ownerName = [p.owner_firstname, p.owner_lastname].filter(Boolean).join(" ");
                    return (
                      <tr key={p.id}>
                        <td>
                          <div style={{ fontWeight: 600, color: "var(--adm-navy)" }}>{p.property_name || `Property #${p.id}`}</div>
                          <div style={{ fontSize: ".78rem", color: "var(--adm-muted)" }}>{p.address || "Address not available"}</div>
                        </td>
                        <td>
                          <div style={{ fontWeight: 600 }}>{ownerName || `User #${p.user_id}`}</div>
                          <div style={{ fontSize: ".78rem", color: "var(--adm-muted)" }}>{p.owner_email || "-"}</div>
                        </td>
                        <td style={{ textTransform: "capitalize" }}>{p.status || "-"}</td>
                        <td>{p.price ? `Rs ${Number(p.price).toLocaleString()}` : "-"}</td>
                        <td>
                          {canDelete ? (
                            <button
                              className="adm-btn danger sm"
                              onClick={() => handleDelete(p)}
                              disabled={deletingId === p.id}
                            >
                              <FontAwesomeIcon icon={faTrash} /> Delete
                            </button>
                          ) : (
                            <span style={{ fontSize: ".76rem", color: "var(--adm-muted)" }}>Read only</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="adm-pagination">
              <span>Page {page} of {totalPages}</span>
              <div className="adm-pag-btns">
                <button className="adm-pag-btn" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>← Prev</button>
                <button className="adm-pag-btn" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Next →</button>
              </div>
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
}
